'use client';

import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { initMixpanel, trackPageView, trackEvent, identifyUser, setUserProperties, resetUser, getCurrentUserId, getCurrentDeviceId, getIDMergeStatus } from '@/lib/mixpanel';
import { logWarn } from '@/lib/logger';

type MixpanelProperties = Record<string, unknown>;

interface MixpanelContextType {
  trackPageView: (pageName: string, properties?: MixpanelProperties) => void;
  trackEvent: (eventName: string, properties?: MixpanelProperties) => void;
  identifyUser: (userId: string, properties?: MixpanelProperties) => void;
  setUserProperties: (properties: MixpanelProperties) => void;
  resetUser: () => void;
  getCurrentUserId: () => string | null;
  getCurrentDeviceId: () => string | null;
  getIDMergeStatus: () => unknown;
}

const MixpanelContext = createContext<MixpanelContextType | null>(null);

export const useMixpanel = () => {
  const context = useContext(MixpanelContext);
  if (!context) {
    logWarn('useMixpanel must be used within a MixpanelProvider');
    // Return a no-op object to prevent crashes
    return {
      trackPageView: () => {},
      trackEvent: () => {},
      identifyUser: () => {},
      setUserProperties: () => {},
      resetUser: () => {},
      getCurrentUserId: () => null,
      getCurrentDeviceId: () => null,
      getIDMergeStatus: () => null,
    };
  }
  return context;
};

interface MixpanelProviderProps {
  children: ReactNode;
}

export const MixpanelProvider = ({ children }: MixpanelProviderProps) => {
  useEffect(() => {
    // Initialize Mixpanel when the component mounts
    initMixpanel();
  }, []);

  const contextValue: MixpanelContextType = {
    trackPageView,
    trackEvent,
    identifyUser,
    setUserProperties,
    resetUser,
    getCurrentUserId,
    getCurrentDeviceId,
    getIDMergeStatus,
  };

  return (
    <MixpanelContext.Provider value={contextValue}>
      {children}
    </MixpanelContext.Provider>
  );
};
